import castArray from 'lodash/castArray';
import noop from 'lodash/noop';
import toFinite from 'lodash/toFinite';
import { GetServerSidePropsContext } from 'next';
import type { NextPage } from 'next';
import { useMemo } from 'react';
import { readCookieSessionId } from '../../../server/lib/sessionCookie';
import logger from '../../../server/services/logger';
import { cachedFetchPurchase } from '../../api/fetchPurchase';
import { AnnouncesIndexQuery } from '../../api/fetchPurchases';
import { metaTags } from '../../components/_dummyData';
import { useBreadcrumbs } from '../../components/_shared/Breadcrumbs/BreadcrumbsContext';
import Meta from '../../components/Meta';
import { Purchase } from '../../components/Purchase';
import { ServerSideProps } from '../../components/Purchase/Purchase.type';
import { PurchaseProps } from '../../components/Purchase/Purchase.type';
import { ConditionsPurchaseTab } from '../../components/Purchase/PurchaseCard/PurchaseTabs/ConditionsPurchaseTab';
import { CustomerPurchaseTab } from '../../components/Purchase/PurchaseCard/PurchaseTabs/CustomerPurchaseTab';
import { DocsPurchaseTab } from '../../components/Purchase/PurchaseCard/PurchaseTabs/DocsPurchaseTab';
import { LotsPurchaseTab } from '../../components/Purchase/PurchaseCard/PurchaseTabs/LotsPurchaseTab';
import { MainPurchaseTab } from '../../components/Purchase/PurchaseCard/PurchaseTabs/MainPurchaseTab';
import { ProtocolsPurchaseTab } from '../../components/Purchase/PurchaseCard/PurchaseTabs/ProtocolsPurchaseTab';
import { getRegionTitles } from '../../components/Purchases/utils';
import { Services } from '../../types';
import { getReqIpAddress } from '../../utils/getReqIpAddress';

const tabs: PurchaseProps['tabs'] = Object.freeze({
    main: {
        title: 'Общая информация',
        component: MainPurchaseTab,
    },
    lots: {
        title: 'Лоты',
        component: LotsPurchaseTab,
    },
    customer: {
        title: 'Заказчик',
        component: CustomerPurchaseTab,
    },
    docs: {
        title: 'Документы',
        component: DocsPurchaseTab,
    },
    protocols: {
        title: 'Протоколы',
        component: ProtocolsPurchaseTab,
    },
    conditions: {
        title: 'Условия участия',
        component: ConditionsPurchaseTab,
    },
});

const PurchasePage: NextPage<ServerSideProps> = ({
    announce,
    activeTab,
    canPreviewAnnounce,
    reference,
}) => {
    useBreadcrumbs([
        { title: 'закупки', href: '/purchases' },
        { title: `карточка извещения ${announce.number}` },
    ]);

    const Component = tabs?.[activeTab]?.component;

    const tags = useMemo(() => {
        const orgName = announce.organizer.shortName ?? '-';
        const purchaseTitle = announce.title ?? '-';

        const regionTitle = getRegionTitles({
            announce,
            regionsByCode: reference?.regionsByCode,
            placeholder: '-',
        }).join(', ');

        let description = `Тендер: ${
            announce.number ?? '-'
        }. ${purchaseTitle} от ${orgName} в ${regionTitle}. С позициями: `;

        let keywords = `${orgName}, тендер, закупка, ${purchaseTitle}`;

        let count = 0;
        for (const okpd2Code in reference?.okpd2ByCode) {
            // For some reason taking only 5 OKPD2
            if (count < 5) {
                const okpd2Name = reference?.okpd2ByCode[okpd2Code].name ?? '-';
                keywords += `, ${okpd2Name}`;
                description += `${count + 1}. ${okpd2Name}. `;
            }
            count++;
        }

        return {
            title: purchaseTitle,
            description,
            url: `${metaTags.purchases.url}/${announce.id}/main`,
            keywords,
        };
    }, [announce, reference]);

    return (
        <>
            <Meta
                title={tags.title}
                description={tags.description}
                url={tags.url}
                keywords={tags.keywords}
            />
            <Purchase
                announce={announce}
                reference={reference}
                activeTab={activeTab}
                announceId={announce?.id}
                canPreviewAnnounce={canPreviewAnnounce}
                tabs={
                    canPreviewAnnounce
                        ? {
                              main: {
                                  title: 'Общая информация',
                                  component: MainPurchaseTab,
                              },
                          }
                        : tabs
                }
                Component={Component}
            />
        </>
    );
};

export default PurchasePage;

export const getServerSideProps = async (
    context: GetServerSidePropsContext<AnnouncesIndexQuery> & {
        req: { services: Services };
    }
) => {
    const [queryAnnounceId, activeTab] = castArray(context.query.slug);

    const announceId = toFinite(queryAnnounceId);

    if (!announceId) {
        return {
            redirect: {
                permanent: false,
                destination: '/purchases',
            },
        };
    }

    try {
        const sessionId = readCookieSessionId(context.req.headers.cookie);

        const props = await cachedFetchPurchase(
            announceId,
            context.req.services,
            sessionId
        );

        /*
         * Регистрация анонимного просмотра извещения
         */
        if (activeTab === 'main') {
            context.req.services.apiTenderClient
                .postAnnounceView(
                    { announceId },
                    {
                        clientIpAddress: getReqIpAddress(context.req),
                    }
                )
                .catch(noop);
        }

        if (props.canPreviewAnnounce) {
            return {
                props: {
                    ...props,
                    activeTab: 'main',
                },
            };
        }

        return {
            props: {
                ...props,
                activeTab,
            },
        };
    } catch (error: any) {
        logger.error(error);
        return {
            redirect: {
                permanent: false,
                destination: '/purchases',
            },
        };
    }
};
